import { RadioProps, useRadio, VisuallyHidden } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import type { ReturnsProductDetailsResponseVariantInfo } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import { hasVariantStock } from '../helpers/has-variant-stock'
import { ShopIcon } from '@/ui/icons/shop'

interface ProductVariantButtonProps extends RadioProps {
  variant: ReturnsProductDetailsResponseVariantInfo
}

export const ProductVariantButton = ({
  variant,
  ...props
}: ProductVariantButtonProps) => {
  const {
    Component,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps
  } = useRadio(props)

  const { t } = useTranslation()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isInStore = useAppSelector(
    (store) => store.settingsApi.settings.response.in_store_settings?.is_enabled
  )

  const isHoff = settings.ecommerce_group === 'hoff'
  const isMisako = settings.ecommerce_group === 'misako'

  const hasStock = hasVariantStock(variant)

  if (isHoff) {
    return (
      <Component
        {...getBaseProps()}
        data-stock={hasStock}
        className="group flex min-h-[58px] cursor-pointer items-center p-2.5 transition-all hover:bg-neutral-100 data-[focus=true]:border-neutral-600 data-[selected=true]:bg-neutral-100"
      >
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>
        <VisuallyHidden>
          <span {...getWrapperProps()}>
            <span {...getControlProps()} />
          </span>
        </VisuallyHidden>
        <div
          {...getLabelWrapperProps()}
          className="flex h-full flex-1 items-center justify-between gap-3"
        >
          <span
            {...getLabelProps()}
            className="!line-clamp-2 inline-block text-sm font-medium group-data-[stock=false]:text-neutral-600 md:text-base"
          >
            {variant.name}
          </span>
          {!hasStock ? (
            <span className="inline-block shrink-0 py-3.5 text-xs font-medium text-neutral-600">
              {t('full_catalog.no_stock')}
            </span>
          ) : isInStore ? (
            <span className="ml-auto flex shrink-0 items-center gap-1.5 rounded-md bg-neutral-200/60 px-1.5 py-0.5 text-xs font-medium">
              <ShopIcon width={14} height={14} />
              {t('full_catalog.in_store_availability')}
            </span>
          ) : null}
        </div>
      </Component>
    )
  }

  if (isMisako) {
    return (
      <Component
        {...getBaseProps()}
        data-stock={hasStock}
        className="group flex min-h-[58px] cursor-pointer items-center border border-solid border-neutral-300 px-3 py-2 transition-all hover:bg-neutral-100 data-[focus=true]:bg-neutral-100 data-[selected=true]:bg-neutral-100"
      >
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>
        <VisuallyHidden>
          <span {...getWrapperProps()}>
            <span {...getControlProps()} />
          </span>
        </VisuallyHidden>
        <div
          {...getLabelWrapperProps()}
          className="flex h-full flex-1 items-center justify-between gap-3"
        >
          <span
            {...getLabelProps()}
            className="!line-clamp-2 inline-block text-sm font-light uppercase tracking-wider group-data-[stock=false]:text-neutral-600"
          >
            {variant.name}
          </span>
          {!hasStock ? (
            <span className="inline-block shrink-0 py-3.5 text-xs font-medium text-neutral-600">
              {t('full_catalog.no_stock')}
            </span>
          ) : isInStore ? (
            <span className="ml-auto flex shrink-0 items-center gap-1.5 rounded-md bg-neutral-200/60 px-1.5 py-0.5 text-xs font-medium">
              <ShopIcon width={14} height={14} />
              {t('full_catalog.in_store_availability')}
            </span>
          ) : null}
        </div>
      </Component>
    )
  }

  return (
    <Component
      {...getBaseProps()}
      data-stock={hasStock}
      className="group flex min-h-[60px] cursor-pointer items-center rounded-md border-1 border-solid border-neutral-300 px-4 pb-1.5 pt-2 transition-all hover:border-neutral-800 hover:bg-neutral-100/60 data-[focus=true]:border-neutral-600 data-[selected=true]:border-neutral-600 data-[stock=false]:bg-neutral-100/80 hover:[&:not(:data-[stock=false])]:data-[selected=true]:bg-white"
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <VisuallyHidden>
        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>
      </VisuallyHidden>
      <div
        {...getLabelWrapperProps()}
        className="flex h-full flex-1 items-center justify-between gap-3"
      >
        <span
          {...getLabelProps()}
          className="!line-clamp-2 inline-block text-sm font-medium group-data-[stock=false]:text-neutral-600 md:text-base"
        >
          {variant.name}
        </span>

        {!hasStock ? (
          <span className="inline-block shrink-0 py-3.5 text-xs font-medium text-neutral-600">
            {t('full_catalog.no_stock')}
          </span>
        ) : isInStore ? (
          <span className="ml-auto flex shrink-0 items-center gap-1.5 rounded-md bg-neutral-200/60 px-1.5 py-0.5 text-xs font-medium">
            <ShopIcon width={14} height={14} />
            {t('full_catalog.in_store_availability')}
          </span>
        ) : null}
      </div>
    </Component>
  )
}
